import { useQuery } from '@tanstack/react-query';
import { tasasApi, type Tasa } from '../lib/api-tasas';

/**
 * Las TASAS DEL DÍA como MARQUESINA LED, bajo el menú y en TODAS las páginas.
 * Pedido de la usuaria (2026-08-21): «lo mismo que enseña AlCambio, pero dentro
 * del Portal Master»; y ese mismo día pidió el formato actual: «un banner móvil
 * […] con información de puntos led aguamarina», fondo azul oscuro marino.
 * Su gemela de la app móvil es `public/movil/tasas.js`.
 *
 * ── DÓNDE VA ────────────────────────────────────────────────────────────────
 *
 * Va DESPUÉS del bloque de cabecera de Marco.tsx (logo + menú píldora) y ANTES
 * del `<div className="flex">`, en flujo normal: se va con el scroll, que es lo
 * que se espera de un letrero, y no le roba altura fija a un teléfono. Desde el
 * rediseño del 2026-08-21 la cabecera ya no es sticky, así que aquí ya no hay
 * 58 px que respetar; el lateral ancla en `top-0`.
 *
 * ── EL EFECTO LED ───────────────────────────────────────────────────────────
 *
 * Vive en `estilos.css` (`.pm-led*`): fondo `--pm-navy` (marino en los dos
 * temas), texto aguamarina con halo, y una rejilla superpuesta que "pixela"
 * el texto como un panel de puntos. La pista va DUPLICADA (la segunda copia
 * con `aria-hidden`) y la animación corre hasta −50 %: bucle continuo sin
 * salto. Pararse encima la pausa, para leer y para ver los `title`.
 *
 * ── SIN CIFRAS INVENTADAS ───────────────────────────────────────────────────
 *
 * Se pinta LO QUE VINO. El servidor manda solo las tasas que pudo traer y las
 * que no, en `faltantes`; aquí no se rellena el hueco con una parecida ni se
 * reusa la de la carga anterior. Si no vino ninguna, el letrero dice que no
 * hay dato en vez de desaparecer sin más: desaparecer se lee como «no está
 * desplegado» y manda a buscar un fallo donde no lo hay.
 *
 * La ventana de refresco (15 min) acompaña al TTL del servidor (10 min): el
 * BCV publica una vez al día y el P2P se mueve todo el tiempo, pero un letrero
 * decorativo no justifica más tráfico que eso.
 */

const NF2 = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NF1 = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const HORA = new Intl.DateTimeFormat('es-VE', { hour: '2-digit', minute: '2-digit' });

/** La fecha de la tasa en texto corto. Las del BCV vienen «YYYY-MM-DD» a secas. */
function cuando(fecha: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    const [a, m, d] = fecha.split('-');
    return `${d}/${m}/${a}`;
  }
  const t = new Date(fecha);
  return Number.isNaN(t.getTime()) ? fecha : `${HORA.format(t)}`;
}

/**
 * El sello de la fecha. Las del BCV (las «YYYY-MM-DD» a secas) son la FECHA
 * VALOR —el día en que rige esa publicación—, y el BCV publica cada día hábil
 * con la del siguiente: el 14/09 su portada ya decía «Fecha Valor: Martes, 15
 * Septiembre 2026». Rotular eso «Dato del 15/09» un día 14 se lee como un error
 * de la tira; «Fecha valor» es el término del propio BCV y es lo que la usuaria
 * tiene delante en bcv.org.ve. Las del P2P, que traen hora, siguen igual.
 */
function sello(fecha: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(fecha)
    ? `Fecha valor ${cuando(fecha)}`
    : `Dato del ${cuando(fecha)}`;
}

/** Un tramo del letrero. El `title` sigue contestando «¿de dónde salió esta
 *  cifra?»: fuente, fecha y las dos puntas del P2P cuando las hay. */
function Tramo({ t }: { t: Tasa }) {
  const sube = t.variacion !== null && t.variacion > 0.00005;
  const baja = t.variacion !== null && t.variacion < -0.00005;
  const rotulo = [
    `${t.etiqueta} · ${NF2.format(t.valor)} ${t.unidad}`,
    t.detalle,
    `Fuente: ${t.fuente}`,
    sello(t.fecha),
    t.compra !== null && t.venta !== null
      ? `Compra ${NF2.format(t.compra)} · Venta ${NF2.format(t.venta)}`
      : null,
  ].filter(Boolean).join('\n');

  return (
    <span title={rotulo}>
      {t.etiqueta} {NF2.format(t.valor)} {t.unidad}
      {(sube || baja) && ` ${sube ? '▲' : '▼'}${NF1.format(Math.abs(t.variacion!) * 100)} %`}
    </span>
  );
}

/** El separador entre tramos: puntos de letrero, no texto. */
function Punto() {
  return <span aria-hidden="true" className="px-5 opacity-60">●</span>;
}

export default function TiraTasas() {
  const { data, isError } = useQuery({
    queryKey: ['tasas'],
    queryFn: () => tasasApi.hoy(),
    staleTime: 10 * 60_000,
    refetchInterval: 15 * 60_000,
    refetchOnWindowFocus: true,
  });

  /* Primera carga: nada. Es un letrero decorativo y un esqueleto parpadeando
   * arriba de cada página molesta más de lo que informa. */
  if (!data && !isError) return null;

  if (isError || !data?.tasas.length) {
    return (
      <div className="pm-led">
        <div className="pm-led-texto px-4 text-center">
          Tasas del día · no se pudieron consultar en este momento
        </div>
      </div>
    );
  }

  /* El contenido de UNA vuelta del letrero. Termina en separador para que el
   * empalme con la copia duplicada se lea igual que cualquier otro tramo. */
  const vuelta = (
    <span className="pm-led-texto flex items-center">
      <span className="pl-5 opacity-70">Tasas de hoy</span>
      <Punto />
      {data.tasas.map((t) => (
        <span key={t.clave} className="flex items-center">
          <Tramo t={t} />
          <Punto />
        </span>
      ))}
      {data.brecha !== null && (
        <span
          className="flex items-center"
          title="Cuánto está el paralelo por encima de la tasa oficial del BCV."
        >
          Brecha {NF1.format(data.brecha * 100)} %
          <Punto />
        </span>
      )}
      {/* Lo que NO vino se dice, no se esconde: así una fuente caída se ve. */}
      {data.faltantes.length > 0 && (
        <span className="flex items-center opacity-70">
          Sin dato: {data.faltantes.join(', ')}
          <Punto />
        </span>
      )}
      <span className="flex items-center opacity-70" title={data.aviso}>
        {HORA.format(new Date(data.consultado))} · referencia informativa
        <Punto />
      </span>
    </span>
  );

  return (
    <div className="pm-led" role="marquee" aria-label="Tasas de cambio del día">
      <div className="pm-led-pista">
        {vuelta}
        <span aria-hidden="true">{vuelta}</span>
      </div>
    </div>
  );
}
